"use client";

import { TimelineStep as TimelineStepType } from "@/types/data";
import ScrollAnimation from "../ScrollAnimations";
import ScrollTextAnimation from "../AnimatedText/ScrollTextAnimation";
import AnimatedImage from "../AnimatedImage";
import Image from "next/image";
import { useEffect, useState } from "react";

interface TimelineStepProps {
  step: TimelineStepType;
  isRight?: boolean;
  stepIndex?: number;
}

export default function TimelineStep({
  step,
  isRight = false,
  stepIndex = 0,
}: TimelineStepProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const badgeColorClasses = {
    orange: "bg-[#FFEDD5] text-[#C2410C]",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
  };

  const getStepColor = (index: number) => {
    const colorKeys = Object.keys(badgeColorClasses) as Array<
      keyof typeof badgeColorClasses
    >;
    return colorKeys[index % 3];
  };

  const getMaskStartPosition = (defaultValue: number) => {
    return isMobile ? defaultValue * 0.5 : defaultValue;
  };

  const getImageStartPosition = (defaultValue: number) => {
    return isMobile ? defaultValue * 0.9 : defaultValue;
  };

  return (
    <ScrollAnimation className="timeline-item ">
      <div
        className={`grid lg:grid-cols-2 gap-6 lg:gap-12 items-center ${
          isRight ? "lg:gap-12" : ""
        }`}
      >
        <div
          className={`lg:pr-12 ${
            isRight ? "lg:order-last lg:pr-0 lg:pl-12" : ""
          }`}
        >
          {step.badge && (
            <span
              className={`inline-block ${
                badgeColorClasses[getStepColor(stepIndex)]
              } text-sm font-semibold px-3 py-1 rounded-full mb-4`}
            >
              {step.badge}
            </span>
          )}
          {step.title && (
            <ScrollTextAnimation
              colorInitial="#cfd1d225"
              colorAccent="#001640"
              colorFinal="#001640"
              stagger={0.01}
              speed={5}
            >
              <h2 className="text-4xl leading-[36px] font-semibold mb-4 text-theme-secondary">
                {step.title}
              </h2>
            </ScrollTextAnimation>
          )}
          {step.description && (
            <ScrollTextAnimation
              colorInitial="#b0c0d351"
              colorAccent="#4F5051"
              colorFinal="#4F5051"
              stagger={0.005}
              speed={5}
            >
              <p className="text-[18px] text-[#4F5051] font-medium leading-relaxed">
                {step.description}
              </p>
            </ScrollTextAnimation>
          )}
        </div>

        <div className="relative">
          <div className="hidden lg:block timeline-point">
            {step.id && (
              <span className="font-bold text-[#3B82F6] text-[18px] leading-[27px]">
                {step.id?.toString().padStart(2, "0")}
              </span>
            )}
          </div>
          {step?.image && (
            <AnimatedImage
              className="relative rounded-lg overflow-hidden max-lg:h-[300px] max-lg:w-[300px] mx-auto"
              animationSettings={{
                duration: 0.8,
                imageStartPosition:
                  stepIndex === 1
                    ? getImageStartPosition(-130)
                    : getImageStartPosition(130),
                imageScale: 1,
                maskStartPosition:
                  stepIndex === 1
                    ? getMaskStartPosition(70)
                    : getMaskStartPosition(-70),
                ease: "power2.out",
                threshold: 0.3,
              }}
            >
              <Image
                src={step.image}
                alt={step.imageAlt}
                width={584}
                height={438}
                quality={85}
                priority={stepIndex <= 1}
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 584px"
                className="size-full! object-cover max-h-[400px] object-center"
              />
            </AnimatedImage>
          )}
        </div>
      </div>
    </ScrollAnimation>
  );
}
